import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useDisableFeatureTag, useEnableFeatureTag, useGetTogglableFeatures } from '@sendible/frontendv2/src/models/features';
import { Container } from './index.styles';

const PORTAL_TARGET_ID = 'top-bar-beta-features';

const usePortalTarget = (id: string) => {
  const [target, setTarget] = useState<HTMLElement | null>(() => document.getElementById(id));

  useEffect(() => {
    if (target) return undefined;

    const observer = new MutationObserver(() => {
      const el = document.getElementById(id);

      if (el) {
        setTarget(el);
        observer.disconnect();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [id, target]);

  return target;
};

export const BetaFeatures = () => {
  const target = usePortalTarget(PORTAL_TARGET_ID);
  const { data, isError } = useGetTogglableFeatures();
  const { t } = useTranslation('common_terms');
  const enableFeature = useEnableFeatureTag();
  const disableFeature = useDisableFeatureTag();

  const canSeeBetaFeatures = !isError && data && data.length;

  if (!target || !canSeeBetaFeatures) return null;

  const unifiedCalendarFeature = data.find((feature) => feature.name === 'unified_calendar');

  if (!unifiedCalendarFeature) return null;

  const isUnifiedCalendarEnabled = window.js_featureTagsManager?.isFeatureTag('unifiedCalendar');

  const handleToggleFeature = () => {
    const payload = { featureTagId: unifiedCalendarFeature.id, target: 'user' };

    if (isUnifiedCalendarEnabled) disableFeature.mutate(payload);
    else enableFeature.mutate(payload);

    window.pendo.track('[Publish] Calendar - Status toggled', { status: isUnifiedCalendarEnabled ? 'off' : 'on' });
  };

  return createPortal(
    <>
      <li className="nav-header">{t('beta_features')}</li>
      <Container>
        <span className="beta-feature-label">{t('calendar_view')}</span>
        <button
          id={`calendar-beta-toggle-${isUnifiedCalendarEnabled ? 'on' : 'off'}`}
          className="btn btn-accent btn-small"
          type="button"
          onClick={handleToggleFeature}
        >
          {isUnifiedCalendarEnabled ? t('disable') : t('enable')}
        </button>
      </Container>
    </>,
    target
  );
};
